import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaExternalLinkAlt } from 'react-icons/fa';

const scanLine = keyframes`
  0% { transform: translateY(-100%); }
  50% { transform: translateY(100%); }
  100% { transform: translateY(-100%); }
`;

const slideInFromTop = keyframes`
  from { opacity: 0; transform: translateY(-30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const floatingParticles = keyframes`
  0% { transform: translateY(0) translateX(0) rotate(0); }
  25% { transform: translateY(-10px) translateX(5px) rotate(5deg); }
  50% { transform: translateY(0) translateX(10px) rotate(0); }
  75% { transform: translateY(10px) translateX(5px) rotate(-5deg); }
  100% { transform: translateY(0) translateX(0) rotate(0); }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 15px rgba(100, 182, 255, 0.4); }
  50% { box-shadow: 0 0 30px rgba(100, 182, 255, 0.8), 0 0 50px rgba(0, 255, 204, 0.3); }
  100% { box-shadow: 0 0 15px rgba(100, 182, 255, 0.4); }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(45deg, rgba(0, 0, 0, 0.7), rgba(10, 20, 40, 0.7)),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(100, 182, 255, 0.05) 2px,
        rgba(100, 182, 255, 0.05) 4px
      );
    z-index: -1;
    opacity: 0.8;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(100, 182, 255, 0.05) 50%,
      transparent 100%
    );
    animation: ${scanLine} 8s linear infinite;
    z-index: -1;
    pointer-events: none;
  }
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  margin-bottom: 60px;
  text-align: center;
  letter-spacing: 2px;
  font-size: 2.5rem;
  text-transform: uppercase;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);

  &::before {
    content: '//';
    color: rgba(100, 182, 255, 0.5);
    margin-right: 10px;
  }

  opacity: 0;
  animation: ${slideInFromTop} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: 0.2s;
`;

const LinkButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  background: linear-gradient(135deg, rgba(0, 80, 200, 0.4), rgba(0, 150, 255, 0.3));
  color: #64B6FF;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  letter-spacing: 1px;
  padding: 20px 50px;
  border-radius: 12px;
  border: 1px solid rgba(100, 182, 255, 0.4);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  opacity: 0;
  animation: ${scaleIn} 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, ${glowPulse} 3s infinite;
  animation-delay: 0.5s, 1.5s;

  &:hover {
    background: linear-gradient(135deg, rgba(0, 80, 200, 0.6), rgba(0, 150, 255, 0.5));
    transform: translateY(-3px);
    box-shadow: 0 0 40px rgba(100, 182, 255, 0.8);
    color: #fff;
  }

  svg {
    font-size: 1rem;
  }
`;

const ParticleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size || '4px'};
  height: ${props => props.size || '4px'};
  background: ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  opacity: ${props => props.opacity || 0.5};
  animation: ${floatingParticles} ${props => props.duration || '8s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 5px ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
`;

const particles = [
  { size: '5px', color: 'rgba(100, 182, 255, 0.7)', top: '15%', left: '10%', duration: '9s', delay: '0s', opacity: 0.7 },
  { size: '3px', color: 'rgba(0, 255, 204, 0.6)', top: '25%', left: '20%', duration: '10s', delay: '0.5s', opacity: 0.6 },
  { size: '6px', color: 'rgba(100, 182, 255, 0.5)', top: '60%', left: '85%', duration: '12s', delay: '1s', opacity: 0.5 },
  { size: '4px', color: 'rgba(0, 255, 204, 0.7)', top: '80%', left: '30%', duration: '11s', delay: '1.5s', opacity: 0.7 },
  { size: '7px', color: 'rgba(100, 182, 255, 0.6)', top: '40%', left: '70%', duration: '9s', delay: '2s', opacity: 0.6 },
];

const RivieraSlideSimulador = () => {
  return (
    <SlideWrapper>
      <ParticleContainer>
        {particles.map((p, i) => (
          <Particle key={i} {...p} />
        ))}
      </ParticleContainer>
      <SlideTitle>Simulador</SlideTitle>
      <LinkButton
        href="https://app.abianca.com.br/simulador/09e61be2-b4cf-47d4-8976-c66c2804e267"
        target="_blank"
        rel="noopener noreferrer"
      >
        Abrir Simulador <FaExternalLinkAlt />
      </LinkButton>
    </SlideWrapper>
  );
};

export default RivieraSlideSimulador;
